import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaArrowsAltH } from 'react-icons/fa';
import './BeforeAfterSlider.css';

const BeforeAfterSlider = ({ beforeImage, afterImage, alt = "Before and after comparison" }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // Cancel any pending animation frame when dragging stops
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    // Prevent page scrolling and pull-to-refresh
    e.preventDefault();
    e.stopPropagation();
    
    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!containerRef.current || !e.touches[0]) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    });
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    // Cancel any pending animation frame when dragging stops
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Global event listeners for mouse/touch
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Memoize clip path calculation for performance
  const clipPath = useMemo(() => `inset(0 ${100 - sliderPosition}% 0 0)`, [sliderPosition]);

  return (
    <div 
      ref={containerRef}
      className={`before-after-container ${isDragging ? 'dragging' : ''}`}
      role="img"
      aria-label={alt}
    >
      {/* After Image (Background) */}
      <div className="after-image-container">
        <img 
          src={afterImage} 
          alt="After renovation" 
          className="after-image"
          loading="lazy"
          decoding="async"
        />
        <div 
          className="image-label after-label"
          style={{ opacity: sliderPosition > 85 ? 0 : 1 }}
        >
          After
        </div>
      </div>
      
      {/* Before Image (Clipped) */}
      <div 
        className="before-image-container"
        style={{ clipPath }}
      >
        <img 
          src={beforeImage} 
          alt="Before renovation" 
          className="before-image"
          loading="lazy"
          decoding="async"
        />
        <div 
          className="image-label before-label"
          style={{ opacity: sliderPosition < 15 ? 0 : 1 }}
        >
          Before
        </div>
      </div>
      
      {/* Slider Handle */}
      <div 
        className="slider-handle"
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        role="slider"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={sliderPosition}
        aria-label="Drag to compare before and after images"
        tabIndex={0}
        onKeyDown={useCallback((e) => {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              setSliderPosition(prev => Math.max(0, prev - 5));
              break;
            case 'ArrowRight':
              e.preventDefault();
              setSliderPosition(prev => Math.min(100, prev + 5));
              break;
            case 'Home':
              e.preventDefault();
              setSliderPosition(0);
              break;
            case 'End':
              e.preventDefault();
              setSliderPosition(100);
              break;
            default:
              break;
          }
        }, [])}
      >
        <div className="slider-line"></div>
        <div className="slider-icon">
          <FaArrowsAltH />
        </div>
        <div className="slider-line"></div>
      </div>
      
      {/* Instructions */}
      <div className="slider-instructions">
        <span className="instruction-text">Drag to compare</span>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
